import { useCallback, useEffect, useState } from 'react'

import { useMutation } from '@tanstack/react-query'
import { getRouteApi, useRouter } from '@tanstack/react-router'
import { Request } from 'commons/utils/request'
import { Toast } from 'ui/components'
import { Button } from 'ui/components/Buttons'
import { useConfirmation } from 'ui/components/Dialog/ConfirmationDialogV2'
import { Page, PageFace } from 'ui/components/Page'
import { PanelFace } from 'ui/components/Panel'
import { BSABreadcrumbs } from 'ui/temp'
import { BSA_MODULE_PATH } from '~bank-bsa-reporting/constants'
import { useXMLGeneration } from '~bank-bsa-reporting/hooks/sar'
import { SarItem, SarList } from '~bank-bsa-reporting/types'

import { handlerServerError } from '@orbit/errors/handleServerError'

import { DownloadXMLDialog, Footer, SarTable, Warnings } from './components'

import { TablePanel } from './styles'

const routeApi = getRouteApi('/_app/bsa/sars/')

const SARList = () => {
  const navigate = routeApi.useNavigate()
  const data = routeApi.useLoaderData() as SarList
  const {
    canAddNew,
    items,
    form: { page },
    total
  } = data

  if (page && total === 0 && page > 1) {
    navigate({ to: '.', search: (prev) => ({ ...prev, page: page - 1 }) })
  }

  const [chosenItems, setChosenItems] = useState<SarItem[]>([])
  const [generatedBatch, setGeneratedBatch] = useState<{
    id: number
    name: string
  } | null>(null)
  const [isGeneratingXML, setIsGeneratingXML] = useState(false)
  const [itemToDelete, setItemToDelete] = useState<SarItem | null>(null)
  const [warnings, setWarnings] = useState<string | null>(null)
  const xmlMutation = useXMLGeneration(setGeneratedBatch)
  const router = useRouter()
  const confirmation = useConfirmation()

  const { mutateAsync: deleteSAR } = useMutation<void[], Error, { ids: number[] }>({
    mutationFn: ({ ids }) => Promise.all(ids.map((id) => Request.delete(Request.urls.bank.sar, { id }))),
    onSuccess: () => {
      setChosenItems([])
      setItemToDelete(null)
      router.invalidate()

      const arr = itemToDelete ? [itemToDelete.id] : chosenItems.map((i) => i.id)
      arr.forEach((el) => Toast.successSmall(`SAR #${el} was successfully deleted.`))
    },
    onError: (e) => handlerServerError(e)
  })

  const onItemSelect = (item?: SarItem) => {
    if (!item) {
      if (chosenItems.length < (items?.length || 0)) {
        setChosenItems(items)
      } else {
        setChosenItems([])
      }
    } else {
      const index = chosenItems.findIndex((i) => i.id === item.id)
      if (index === -1) {
        setChosenItems(chosenItems.concat(item))
      } else {
        const copy = [...chosenItems]
        copy.splice(index, 1)
        setChosenItems(copy)
      }
    }
  }

  const startGenerateXML = async () => {
    await confirmation.showAskConfirmation({
      message: (
        <span>
          Are you sure you want to generate Batch from&nbsp;
          <i>{chosenItems.length === 1 ? chosenItems[0].id : 'these'} SAR(s)?</i>
        </span>
      ),
      submitButtonText: 'Generate',
      confirmCallback: async () => {
        setIsGeneratingXML(true)
        xmlMutation
          .mutateAsync({ ids: chosenItems.map((i) => i.id) })
          .then(() => {
            setChosenItems([])
          })
          .finally(() => {
            setIsGeneratingXML(false)
          })
      }
    })
  }

  const startDeleting = useCallback(async () => {
    if (
      await confirmation.showDeleteConfirmation({
        message: (
          <span>
            Are you sure you want to delete&nbsp;
            <i>{itemToDelete || chosenItems.length === 1 ? (itemToDelete || chosenItems[0]).id : 'these'} SAR(s)?</i>
          </span>
        ),
        submitButtonText: 'Delete'
      })
    ) {
      deleteSAR({ ids: itemToDelete ? [itemToDelete.id] : chosenItems.map((i) => i.id) })
    } else {
      setChosenItems([])
      setItemToDelete(null)
    }
  }, [chosenItems, confirmation, itemToDelete, router])

  const onCloseXMLDialog = () => {
    if (isGeneratingXML) {
      return
    }

    setGeneratedBatch(null)
  }

  useEffect(() => {
    if (itemToDelete?.id) {
      startDeleting()
    }
  }, [itemToDelete])

  const onShowWarnings = useCallback(
    (data: string) => {
      setWarnings(data)
    },
    [setWarnings]
  )

  return (
    <Page
      face={PageFace.PRIMARY}
      title={<BSABreadcrumbs title="SARs List" parentUrl={BSA_MODULE_PATH} parentTitle="BSA Reporting" />}
      actions={
        canAddNew && (
          <Button
            face="tertiary"
            onClick={() => navigate({ to: '/bsa/sars/$id/edit', params: { id: '0' } })}
            data-qa="New SAR"
          >
            New SAR
          </Button>
        )
      }
    >
      <Warnings warnings={warnings} onClose={() => setWarnings(null)} />
      <DownloadXMLDialog batch={generatedBatch} isGenerating={isGeneratingXML} onClose={onCloseXMLDialog} />
      <TablePanel rounded shadowed collapsible={false} face={PanelFace.SECONDARY}>
        <SarTable
          onItemSelect={onItemSelect}
          onDelete={setItemToDelete}
          onShowWarnings={onShowWarnings}
          chosenItems={chosenItems}
          data={data}
        />
      </TablePanel>
      <Footer hasChosenItems={chosenItems.length > 0} onDeleteSARs={startDeleting} onXMLGenerate={startGenerateXML} />
    </Page>
  )
}

export default SARList
